import { ArrowUpRight, User } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Components } from 'react-virtuoso'
import { Virtuoso } from 'react-virtuoso'

import { paths } from 'src/config/paths'
import { SCROLL_VELOCITY_THRESHOLD } from 'src/lib/constants'
import { sendEvent } from 'src/shared/api/events/send-event'
import NoSearchIcon from 'src/shared/assets/icons/utils/no-search.png'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { Avatar, AvatarImage, AvatarFallback } from 'src/shared/components/ui/miscellaneous/avatar'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { Spinner } from 'src/shared/components/ui/miscellaneous/spinner'
import { DelayedSuspense } from 'src/shared/components/utils/delayed-suspense'
import { useUsersBySearch } from 'src/shared/features/users/api/accounts/use-users-by-search'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import { useEventStore } from 'src/shared/store/event-store'

interface UserItemProps {
  user: PublicUserData
  onClick: (user: PublicUserData) => void
}

export const UserItem = ({ user, onClick }: UserItemProps) => {
  return (
    <button
      onClick={() => onClick(user)}
      className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/50"
    >
      <div className="flex w-full items-center justify-between gap-3">
        <Avatar className="size-12">
          <AvatarImage src={user.profilePhoto} alt={user.username} />
          <AvatarFallback>
            <User className="size-6" />
          </AvatarFallback>
        </Avatar>

        <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
          <div className="flex w-full items-center gap-2 truncate">
            <p className="truncate text-base font-medium">
              {user.firstName} {user.lastName}
            </p>
            <p className="shrink-0 text-xs text-muted">•</p>
            <p className="shrink-0 text-sm text-muted">@{user.username}</p>
          </div>
          <p className="flex shrink-0 items-center gap-1 text-sm text-muted">
            {professionMap[user.profession as Profession].label}
          </p>
        </div>
        <ArrowUpRight className="ml-3 size-5 text-muted" />
      </div>
    </button>
  )
}

const ScrollSeekPlaceholder: Components['ScrollSeekPlaceholder'] = ({ height }) => (
  <div style={{ height }} className="px-4 py-3">
    <div className="flex w-full items-center justify-between gap-3">
      <Skeleton className="size-12 rounded-full" />
      <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
        <div className="flex w-full items-center gap-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="ml-3 size-5" />
    </div>
  </div>
)

const Placeholder = () => {
  return (
    <div className="flex w-full flex-col divide-y">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex w-full items-center justify-between px-4 py-3">
          <div className="flex w-full items-center justify-between gap-3">
            <Skeleton className="size-12 rounded-full" />
            <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
              <div className="flex w-full items-center gap-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-24" />
              </div>
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="ml-3 size-5" />
          </div>
        </div>
      ))}
    </div>
  )
}

const EmptyState = ({ query }: { query: string }) => {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-8 text-center text-muted">
      <img src={NoSearchIcon} alt="No results" className="size-24" />
      <div>
        <p className="text-lg font-medium">
          No results for <span className="font-bold text-foreground">{query}</span>
        </p>
        <p className="text-sm">Try adjusting your search terms</p>
      </div>
    </div>
  )
}

const UserSearchResultsContent = ({
  query,
  onClick,
  height = 'calc(100vh-170px)',
}: {
  query: string
  onClick?: (user: PublicUserData) => void
  height?: string
}) => {
  const navigate = useNavigate()
  const { data: searchResults, hasNextPage, fetchNextPage, isFetchingNextPage } = useUsersBySearch(6, query)

  const users = useMemo(() => {
    const allUsers = searchResults?.pages.flatMap((page) => page.results) || []
    return [...new Map(allUsers.map((user) => [user.id, user])).values()]
  }, [searchResults])

  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const setFlowId = eventStore.setFlowId
  const setSessionId = eventStore.setSessionId

  const handleCardClick = (user: PublicUserData) => {
    if (onClick) {
      onClick(user)
      return
    }

    // Default navigation behavior
    if (!sessionId) {
      setSessionId()
    }
    const newFlowId = setFlowId()
    sendEvent({
      userId: user.id,
      eventType: 'flow_start',
      info: {
        sessionId,
        flow_type: 'send_referral',
        flow_step: 'select_searched_user',
        flow_id: newFlowId,
      },
    })
    navigate(paths.users.profile.getHref(user.username))
  }

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  if (!users.length) {
    return <EmptyState query={query} />
  }

  const Footer = () => {
    if (isFetchingNextPage) {
      return (
        <div className="flex h-20 w-full justify-center">
          <Spinner />
        </div>
      )
    }
    return null
  }

  return (
    <div className="h-[calc(100vh-170px)]" style={{ height }}>
      <Virtuoso
        data={users}
        endReached={loadMore}
        overscan={200}
        itemContent={(index, user: PublicUserData) => <UserItem key={user.id} user={user} onClick={handleCardClick} />}
        components={{
          Footer,
          ScrollSeekPlaceholder,
        }}
        scrollSeekConfiguration={{
          enter: (velocity) => Math.abs(velocity) > SCROLL_VELOCITY_THRESHOLD,
          exit: (velocity) => Math.abs(velocity) < 10,
        }}
        style={{ height: '100%', scrollbarWidth: 'none' }}
      />
    </div>
  )
}

export const UserSearchResults = ({
  query,
  onClick,
  height,
}: {
  query: string
  onClick?: (user: PublicUserData) => void
  height?: string
}) => {
  return (
    <ErrorBoundaryWrapper title="" description="" fallback={<div />}>
      <DelayedSuspense fallback={<Placeholder />}>
        <UserSearchResultsContent query={query} onClick={onClick} height={height} />
      </DelayedSuspense>
    </ErrorBoundaryWrapper>
  )
}
